/** Build the window.TheSocialTap public API object. */

import { state, debugLog } from "./state.js";
import { sendEvent, replayBufferedEvents } from "./event-transport.js";
import { checkAndFireEvent, fetchConfig } from "./config.js";
import { reinitPixelUserData } from "./facebook-pixel.js";
import { refreshPlatformPixelUserData } from "./platforms.js";
import { persistUserData } from "./user-data-store.js";
import { captureUserDataFields } from "./pii-capture.js";
import { DEFAULT_CURRENCY } from "./constants.js";
import { buildCustomData } from "./custom-params.js";
import { codeRuleEvent, findCodeRule, ruleReference } from "./code-rules.js";
import { shouldSkipForRule } from "./conversion-dedup.js";
import { declareConsent, runConsentChecks } from "./consent.js";
import { claimConversion } from "./conversion-claims.js";
import { authoritativeEcommerceClaimKey, authoritativeEcommerceEventId } from "./data-layer-event-names.js";
import type { ConsentChoice, PublicApi } from "./types.js";

function trackEvent(eventName: string, valueOrParams?: number | Record<string, unknown>, currency?: string,
  eventId?: string): void {
  const data: Record<string, unknown> = { eventName };
  if (valueOrParams && typeof valueOrParams === "object") {
    data.eventValue = valueOrParams.value;
    data.currency = valueOrParams.currency || DEFAULT_CURRENCY;
    const customData = buildCustomData(valueOrParams);
    if (customData) data.customData = customData;
  } else {
    data.eventValue = valueOrParams;
    data.currency = currency || DEFAULT_CURRENCY;
  }
  if (eventId) data.eventId = eventId;
  sendEvent(data as Parameters<typeof sendEvent>[0]);
}

/**
 * track() naming a code rule. An unknown, disabled or deleted rule sends
 * nothing and says so in the console: sending it anyway would deliver it to
 * every platform, which is exactly what the rule was made to prevent.
 */
function trackCodeRule(eventName: string, reference: string, params: Record<string, unknown>): void {
  if (!state.configSettled) {
    state.pendingCommands.push({ m: "track", a: [eventName, params] });
    debugLog("codeRule", "buffered until config settles", reference);
    return;
  }
  const rule = findCodeRule(state.config?.rules, reference);
  if (!rule) {
    console.warn("[TheSocialTap] track(\"" + eventName + "\"): no active code rule " + reference + ", event not sent.");
    return;
  }
  if (shouldSkipForRule(rule.id, rule.oncePerVisitor)) return;
  sendEvent(codeRuleEvent(rule, params));
}

export function buildPublicApi(): PublicApi {
  const api: PublicApi = {
    init(websiteId: string): void {
      if (state.initialized) return;

      debugLog("init", "href=" + window.location.href, "websiteId=" + websiteId);

      state.websiteId = websiteId;

      // Replay any buffered events from previous failed sends
      replayBufferedEvents();

      // Fetch config and auto-track
      fetchConfig((err, _config, firedPending) => {
        debugLog("init callback", "err=" + !!err, "firedPending=" + !!firedPending, "href=" + window.location.href, "pendingUrls=", state.pendingUrls.slice());
        if (err) {
          sendEvent({ eventName: "PageView" });
        } else if (!firedPending) {
          checkAndFireEvent(window.location.href);
        }
        // Set lastTrackedUrl so SPA hooks don't re-fire for the initial page
        state.lastTrackedUrl = window.location.href;
      });

      state.initialized = true;
    },

    pageView(): void {
      if (state.config && state.config.trackingEnabled) {
        checkAndFireEvent(window.location.href);
      } else {
        sendEvent({ eventName: "PageView" });
      }
    },

    track(
      eventName: string,
      valueOrParams?: number | Record<string, unknown>,
      currency?: string,
    ): void {
      const reference = ruleReference(valueOrParams);
      if (reference === null) {
        trackEvent(eventName, valueOrParams, currency);
        return;
      }
      trackCodeRule(eventName, reference, valueOrParams as Record<string, unknown>);
    },

    /**
     * An e-commerce event from a source that KNOWS, such as our WooCommerce
     * plugin reading the order out of WooCommerce itself.
     *
     * Same wire as track(), one difference that matters: it takes the claim.
     * The DOM heuristic and the dataLayer bridge both observe the same product
     * view, and whoever claims first sends while the other stands down. A
     * plugin that called plain track() would sit outside that agreement and
     * hand the platform two of everything.
     *
     * The heuristics stay switched on behind it. A plugin that misses a case
     * then still gets caught by them, which is the right way round: guessing is
     * the fallback, not the source.
     */
    trackEcommerce(eventName: string, params?: Record<string, unknown>, actionId?: string): void {
      const orderId = typeof params?.order_id === "string" ? params.order_id : undefined;
      if (!claimConversion(eventName, authoritativeEcommerceClaimKey(eventName, orderId, window.location.href, actionId))) {
        debugLog("trackEcommerce", "skipped " + eventName + " — already claimed");
        return;
      }
      trackEvent(eventName, params, undefined, authoritativeEcommerceEventId(eventName, actionId));
    },

    trackWithPixel(eventName: string, value?: number, currency?: string): void {
      sendEvent({
        eventName,
        eventValue: value,
        currency: currency || DEFAULT_CURRENCY,
        browserPixelFired: true,
      });
    },

    /**
     * Normalize, hash and store user data fields for the server-side APIs.
     *
     * All PII fields (email, phone, name, city, ...) are normalized per the
     * platform variant contract (pii-capture.ts) and SHA-256 hashed before
     * storage. The externalId is stored as-is because it is already an
     * opaque, non-PII identifier (adapters hash it where a platform requires).
     */
    setUserData(data: Record<string, string>): Promise<void> {
      return captureUserDataFields(data).then(() => {
        persistUserData();
        reinitPixelUserData();
        refreshPlatformPixelUserData();
      });
    },

    consent(next) {
      // Eén waarde voor allebei is de gewone situatie: een banner met één knop
      // "accepteren" zegt niets aparts over analytics en marketing. Wie wel
      // aparte categorieën heeft geeft een object mee, en dan geldt alleen wat
      // daarin staat; wat ontbreekt is niet toegestaan.
      let choice: { analytics: ConsentChoice; marketing: ConsentChoice };
      if (typeof next === "string") {
        choice = { analytics: next, marketing: next };
      } else {
        choice = {
          analytics: next.analytics ?? "denied",
          marketing: next.marketing ?? "denied",
        };
      }

      declareConsent(choice);

      // Meteen laten gelden, niet pas bij de volgende paginaweergave. De
      // bewaakte hercontrole draait op een interval van twee seconden, en dat
      // is precies lang genoeg om de klik die op "accepteren" volgde te missen.
      runConsentChecks();
    },

    getConfig() {
      return state.config;
    },

    checkUrl(url?: string) {
      checkAndFireEvent(url || window.location.href);
    },

    isReady() {
      return state.initialized && state.config !== null;
    },
  };

  return api;
}
